import { create } from "zustand";
import { persist } from "zustand/middleware";
import useChatStore from "./chatStore";
import { clearOfflineData } from "../offline/offlineSync";

const API_BASE = import.meta.env.VITE_API_URL;

// The server keeps a single current organization/project per account, so
// another tab or device may have switched it since this tab loaded. Given
// fresh /me data, put this tab's own selection back on the server (if the
// user still has access to it) and return /me data that matches it.
const restoreTabSelection = async (token, data, local) => {
  const localOrgId = local.user?.currentOrganization;
  const localProjectId = local.currentProject?._id;
  const serverOrgId = data.user?.currentOrganization;
  const serverProjectId = data.currentProject?._id;

  const sameSelection =
    localOrgId === serverOrgId &&
    (!localProjectId || localProjectId === serverProjectId);
  const stillMember = data.organizations?.some((org) => org._id === localOrgId);
  if (!localOrgId || sameSelection || !stillMember) return data;

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
  try {
    if (localOrgId !== serverOrgId) {
      const res = await fetch(`${API_BASE}/api/auth/switch-organization`, {
        method: "POST",
        headers,
        body: JSON.stringify({ organizationId: localOrgId }),
      });
      if (!res.ok) return data;
    }
    if (localProjectId) {
      // Fails (and keeps the organization's default project) if the project
      // was deleted or the user was removed from it
      await fetch(`${API_BASE}/api/auth/switch-project`, {
        method: "POST",
        headers,
        body: JSON.stringify({ projectId: localProjectId }),
      });
    }
    const res = await fetch(`${API_BASE}/api/auth/me`, { headers });
    return res.ok ? await res.json() : data;
  } catch {
    return data;
  }
};

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      organizations: [],
      projects: [],
      currentProject: null,
      selectedNote: null,
      _hasHydrated: false,
      _justLoggedIn: false, // Flag to skip auth check right after login

      login: async (email, password) => {
        try {
          const res = await fetch(`${API_BASE}/api/auth/login`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ email, password }),
          });

          if (!res.ok) {
            const { message } = await res.json();
            return { success: false, message };
          }

          const { token, user, organizations, projects, currentProject } =
            await res.json();

          set({
            token,
            user,
            organizations,
            projects,
            currentProject,
            _justLoggedIn: true, // Skip next auth check
          });

          return { success: true };
        } catch (err) {
          console.error("Login failed:", err);
          return { success: false, message: "Login failed" };
        }
      },

      logout: () => {
        // Clear chat data when logging out
        useChatStore.getState().clearChatData();
        // Cached data and offline changes belong to this user
        clearOfflineData();

        set({
          token: null,
          user: null,
          organizations: [],
          projects: [],
          currentProject: null,
          selectedNote: null,
        });
      },

      signup: async (email, password) => {
        try {
          const res = await fetch(`${API_BASE}/api/auth/signup`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ email, password }),
          });

          if (!res.ok) {
            const { message } = await res.json();
            return { success: false, message };
          }

          return { success: true };
        } catch (err) {
          return { success: false, message: "Signup failed" };
        }
      },

      checkAuth: async () => {
        const token = get().token;
        if (!token) return;

        // Skip auth check if we just logged in (data is fresh)
        if (get()._justLoggedIn) {
          set({ _justLoggedIn: false });
          return;
        }

        try {
          const res = await fetch(`${API_BASE}/api/auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!res.ok) {
            // Server errors (e.g. during a deploy) don't mean the session is
            // gone - keep the current state and check again later
            if (res.status !== 401) return;

            // The session is no longer valid - clear state and go to login
            get().logout();
            // Only redirect if not already on login/signup pages
            const currentPath = window.location.hash;
            if (
              !currentPath.includes("/login") &&
              !currentPath.includes("/signup") &&
              !currentPath.includes("/accept-invite")
            ) {
              window.location.href = "/#/login";
            }
            return;
          }

          const { user, organizations, projects, currentProject } =
            await restoreTabSelection(token, await res.json(), get());

          set({
            user,
            organizations,
            projects,
            currentProject,
          });
        } catch (err) {
          // Network error (e.g. a laptop waking up before Wi-Fi is back) -
          // keep the user logged in; the next check will try again
          console.error("Session check failed:", err);
        }
      },

      // Refresh user data without redirect logic (for invite acceptance)
      refreshUserData: async () => {
        const token = get().token;
        if (!token) return false;

        try {
          const res = await fetch(`${API_BASE}/api/auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!res.ok) {
            console.error("Failed to refresh user data, status:", res.status);
            return false;
          }

          const { user, organizations, projects, currentProject } =
            await res.json();

          set({
            user,
            organizations,
            projects,
            currentProject,
          });

          return true;
        } catch (err) {
          console.error("Error refreshing user data:", err);
          return false;
        }
      },

      fetchProjects: async () => {
        const user = get().user;
        const token = get().token;
        const userId = user?._id || user?.id;
        const organizationId = user?.currentOrganization;

        if (!userId || !token) {
          return;
        }

        try {
          let url = `${API_BASE}/api/projects?userId=${userId}`;

          // Add organizationId filter if available
          if (organizationId) {
            url += `&organizationId=${organizationId}`;
          }

          const res = await fetch(url, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!res.ok) {
            throw new Error("Failed to fetch projects");
          }

          const projects = await res.json();

          // Update currentProject if it exists in the fresh projects
          const currentProject = get().currentProject;
          if (currentProject) {
            const updatedCurrentProject = projects.find(
              (p) => p._id === currentProject._id || p.id === currentProject.id,
            );
            if (updatedCurrentProject) {
              set({ projects, currentProject: updatedCurrentProject });
            } else {
              set({ projects });
            }
          } else {
            set({ projects });
          }

          return projects;
        } catch (error) {
          console.error("Error fetching projects:", error);
          return [];
        }
      },

      fetchOrganizations: async () => {
        const token = get().token;
        if (!token) {
          return [];
        }

        try {
          const res = await fetch(`${API_BASE}/api/organizations`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!res.ok) {
            const errorText = await res.text();
            console.error(
              "Failed to fetch organizations:",
              res.status,
              errorText,
            );

            // If unauthorized, logout and redirect
            if (res.status === 401) {
              get().logout();
              window.location.href = "/#/login";
            }

            throw new Error("Failed to fetch organizations");
          }

          const organizations = await res.json();

          set({ organizations });

          return organizations;
        } catch (error) {
          console.error("Error fetching organizations:", error);
          return [];
        }
      },

      setCurrentProject: async (project) => {
        // Ensure project has both id and _id fields for consistency
        const normalizedProject = project
          ? {
              ...project,
              id: project.id || project._id,
              _id: project._id || project.id,
            }
          : null;

        set({ currentProject: normalizedProject });

        // Persist the change to the server
        if (normalizedProject) {
          const token = get().token;
          if (token) {
            try {
              await fetch(`${API_BASE}/api/auth/switch-project`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ projectId: normalizedProject._id }),
              });
              // We don't need to update state from response since we already set it above
            } catch (error) {
              console.error(
                "Failed to persist project change to server:",
                error,
              );
              // Still keep the local change even if server update fails
            }
          }
        }
      },

      setSelectedNote: (note) => {
        set({ selectedNote: note });
      },

      updateUser: (updatedUser) => {
        set({ user: { ...get().user, ...updatedUser } });
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        organizations: state.organizations,
        projects: state.projects,
        currentProject: state.currentProject,
        selectedNote: state.selectedNote,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state._hasHydrated = true;
        }
      },
    },
  ),
);

export default useAuthStore;
